
import * as THREE from 'three';

import { Editor } from './js/Editor.js';
import { Viewport } from './js/Viewport.js';
import { Toolbar } from './js/Toolbar.js';
import { Script } from './js/Script.js';
import { Player } from './js/Player.js';
import { Sidebar } from './js/Sidebar.js';
import { Menubar } from './js/Menubar.js';
import { Resizer } from './js/Resizer.js';
import { InteractionEditorWindow } from './js/InteractionEditorWindow.jsx';
import { AddObjectCommand } from './js/commands/AddObjectCommand.js';
import { AuthChecker } from './js/auth/AuthChecker.js';
import { AuthOverlay } from './js/auth/AuthOverlay.js';

window.URL = window.URL || window.webkitURL;
window.BlobBuilder = window.BlobBuilder || window.WebKitBlobBuilder || window.MozBlobBuilder;

//

// Initialize auth checker
const authChecker = new AuthChecker();
const authOverlay = new AuthOverlay();

// Check authentication before loading editor
const authResult = await authChecker.checkSession();

// Always initialize editor first so UI exists
const editor = new Editor();

window.editor = editor; // Expose editor to Console - Vite setup complete!
window.THREE = THREE; // Expose THREE to APP Scripts and Console

// Create main container wrapper
const editorContainer = document.createElement( 'div' );
editorContainer.className = 'editor-container';
document.body.appendChild( editorContainer );

// Create menubar (spans full width at top)
const menubar = new Menubar( editor );
editorContainer.appendChild( menubar.dom );

// Create main content wrapper for flex layout
const mainContent = document.createElement( 'div' );
mainContent.className = 'main-content';
editorContainer.appendChild( mainContent );

// Create left container for Scene + Interaction Editor (vertical split)
const leftContainer = document.createElement( 'div' );
leftContainer.className = 'left-container';
leftContainer.style.cssText = `
	flex: 1;
	display: flex;
	flex-direction: column;
	min-width: 400px;
`;

// Create viewport with container wrapper (takes full width of left container)
const viewportWrapper = document.createElement( 'div' );
viewportWrapper.className = 'viewport-wrapper panel-container';
viewportWrapper.style.cssText = `
	flex: none;
	height: calc(100% - 300px);
	display: flex;
	flex-direction: column;
	min-height: 200px;
`;
const viewport = new Viewport( editor );
viewportWrapper.appendChild( viewport.dom );

// Add toolbar inside viewport wrapper
const toolbar = new Toolbar( editor );
viewportWrapper.appendChild( toolbar.dom );

leftContainer.appendChild( viewportWrapper );

// Create vertical resizer handle (between scene and interaction editor)
const verticalResizerHandle = document.createElement( 'div' );
verticalResizerHandle.className = 'vertical-panel-resizer';
verticalResizerHandle.style.cssText = `
	height: 8px;
	width: 100%;
	cursor: ns-resize;
	background-color: transparent;
	display: block;
	user-select: none;
	-webkit-user-select: none;
`;
leftContainer.appendChild( verticalResizerHandle );

mainContent.appendChild( leftContainer );

// Create horizontal resizer handle (between left container and sidebar)
const horizontalResizerHandle = document.createElement( 'div' );
horizontalResizerHandle.className = 'horizontal-panel-resizer';
horizontalResizerHandle.style.cssText = `
	width: 8px;
	height: 100%;
	cursor: col-resize;
	background-color: transparent;
	user-select: none;
	-webkit-user-select: none;
`;
mainContent.appendChild( horizontalResizerHandle );

// Create sidebar with container wrapper
const sidebarWrapper = document.createElement( 'div' );
sidebarWrapper.className = 'sidebar-wrapper panel-container';
sidebarWrapper.style.width = '350px';
const sidebar = new Sidebar( editor );
sidebarWrapper.appendChild( sidebar.dom );
mainContent.appendChild( sidebarWrapper );

// Restore saved layout state
const savedSidebarWidth = editor.config.getKey( 'layout/sidebarWidth' );
const savedViewportHeight = editor.config.getKey( 'layout/viewportHeight' );

if ( savedSidebarWidth ) {
	sidebarWrapper.style.width = savedSidebarWidth + 'px';
}

if ( savedViewportHeight ) {
	viewportWrapper.style.height = savedViewportHeight + 'px';
	viewportWrapper.style.flex = 'none';
}

// Trigger interaction editor canvas resize after layout restoration
setTimeout(() => {
	if ( window.interactionEditor && window.interactionEditor.isOpen() && window.interactionEditor.interactionEditor ) {
		if ( window.interactionEditor.interactionEditor.resize ) {
			window.interactionEditor.interactionEditor.resize();
		}
		if ( window.interactionEditor.interactionEditor.canvas && window.interactionEditor.interactionEditor.canvas.render ) {
			window.interactionEditor.interactionEditor.canvas.render();
		}
	}
}, 100);

// Add resize functionality for horizontal resizer (left container <-> sidebar)
let isResizingHorizontal = false;
let isResizingVertical = false;
let startX = 0;
let startY = 0;
let startWidth = 0;
let startHeight = 0;

function handleHorizontalPointerMove( event ) {
	if ( !isResizingHorizontal || event.isPrimary === false ) return;

	const deltaX = startX - event.clientX;
	const newWidth = Math.min( Math.max( startWidth + deltaX, 250 ), 600 );
	sidebarWrapper.style.width = newWidth + 'px';

	editor.signals.windowResize.dispatch();
	event.preventDefault();
}

function handleHorizontalPointerUp( event ) {
	if ( !isResizingHorizontal || event.isPrimary === false ) return;

	isResizingHorizontal = false;
	document.body.style.cursor = '';

	// Save sidebar width to config
	editor.config.setKey( 'layout/sidebarWidth', sidebarWrapper.offsetWidth );

	document.removeEventListener( 'pointermove', handleHorizontalPointerMove );
	document.removeEventListener( 'pointerup', handleHorizontalPointerUp );

	event.preventDefault();
}

horizontalResizerHandle.addEventListener( 'pointerdown', function( event ) {
	if ( event.isPrimary === false ) return;
	isResizingHorizontal = true;
	startX = event.clientX;
	startWidth = sidebarWrapper.offsetWidth;
	document.body.style.cursor = 'col-resize';

	document.addEventListener( 'pointermove', handleHorizontalPointerMove );
	document.addEventListener( 'pointerup', handleHorizontalPointerUp );

	event.preventDefault();
});

// Add resize functionality for vertical resizer (scene <-> interaction editor)
function handleVerticalPointerMove( event ) {
	if ( !isResizingVertical || event.isPrimary === false ) return;

	const deltaY = event.clientY - startY;
	const newHeight = Math.min( Math.max( startHeight + deltaY, 200 ), 800 );
	viewportWrapper.style.height = newHeight + 'px';
	viewportWrapper.style.flex = 'none'; // Override flex when manually resized

	// Trigger interaction editor canvas resize if visible
	if ( window.interactionEditor && window.interactionEditor.isOpen() ) {
		if ( window.interactionEditor.interactionEditor && window.interactionEditor.interactionEditor.resize ) {
			setTimeout(() => {
				window.interactionEditor.interactionEditor.resize();
				// Also trigger a render to redraw the patches
				if ( window.interactionEditor.interactionEditor.canvas && window.interactionEditor.interactionEditor.canvas.render ) {
					window.interactionEditor.interactionEditor.canvas.render();
				}
			}, 10);
		}
	}

	editor.signals.windowResize.dispatch();
	event.preventDefault();
}

function handleVerticalPointerUp( event ) {
	if ( !isResizingVertical || event.isPrimary === false ) return;

	isResizingVertical = false;
	document.body.style.cursor = '';

	// Save viewport height to config
	editor.config.setKey( 'layout/viewportHeight', viewportWrapper.offsetHeight );

	document.removeEventListener( 'pointermove', handleVerticalPointerMove );
	document.removeEventListener( 'pointerup', handleVerticalPointerUp );

	event.preventDefault();
}

verticalResizerHandle.addEventListener( 'pointerdown', function( event ) {
	if ( event.isPrimary === false ) return;
	isResizingVertical = true;
	startY = event.clientY;
	startHeight = viewportWrapper.offsetHeight;
	document.body.style.cursor = 'ns-resize';

	document.addEventListener( 'pointermove', handleVerticalPointerMove );
	document.addEventListener( 'pointerup', handleVerticalPointerUp );

	event.preventDefault();
});

// Hidden panels
const script = new Script( editor );
document.body.appendChild( script.dom );

const player = new Player( editor );
document.body.appendChild( player.dom );

const resizer = new Resizer( editor );
document.body.appendChild( resizer.dom );


// Interaction Editor Window integrated into modular layout
const interactionEditor = new InteractionEditorWindow( editor );
leftContainer.appendChild( interactionEditor.getContainer() );

// Expose interaction editor globally for menu access
window.interactionEditor = interactionEditor;

// Set the interaction editor reference in the editor for persistence
editor.interactionEditor = interactionEditor;

// Add a getter for the patch editor to be accessible via editor.patchEditor
Object.defineProperty(editor, 'patchEditor', {
	get: function() {
		return this.interactionEditor && this.interactionEditor.interactionEditor ?
			this.interactionEditor.interactionEditor : null;
	}
});


//

// Handle authentication overlay after editor UI is fully initialized
if (!authResult.authenticated) {
	// Show auth overlay over the fully rendered editor UI
	authOverlay.show();

	// Disable editor interactions until authenticated
	const editorContainer = document.querySelector('.editor-container');
	if (editorContainer) {
		editorContainer.style.pointerEvents = 'none';
	}

	console.log('🔒 Editor loaded in view-only mode - authentication required');
} else {
	// Store session for authenticated users
	window.authSession = authResult.session;
	console.log('✅ Editor loaded with authentication');

	//

	editor.storage.init( function () {

	editor.storage.get( async function ( state ) {

if ( isLoadingFromHash ) return;

if ( state !== undefined ) {

	await editor.fromJSON( state );

} else {

	// Create default scene setup for new users
	const geometry = new THREE.BoxGeometry( 1, 1, 1, 1, 1, 1 );
	const mesh = new THREE.Mesh( geometry, new THREE.MeshStandardMaterial() );
	mesh.name = 'Cube';
	mesh.position.set( 0, 0.5, 0 );

	// Add default lighting
	const light = new THREE.DirectionalLight( 0xffffff, 1 );
	light.name = 'DirectionalLight';
	light.position.set( 5, 8, 3 );

	editor.execute( new AddObjectCommand( editor, mesh ) );
	editor.execute( new AddObjectCommand( editor, light ) );

	// Create default spinning interactions for the cube
	// Delay to ensure interaction editor is fully initialized and sized
	setTimeout(() => {
		editor.createDefaultInteractions();
	}, 100);

}

const selected = editor.config.getKey( 'selected' );

if ( selected !== undefined ) {

	editor.selectByUuid( selected );

}

	} );

	//

	let timeout;

	function saveState() {

if ( editor.config.getKey( 'autosave' ) === false ) {

	return;

}

clearTimeout( timeout );

timeout = setTimeout( function () {

	editor.signals.savingStarted.dispatch();

	timeout = setTimeout( function () {

		editor.storage.set( editor.toJSON() );

		editor.signals.savingFinished.dispatch();

	}, 100 );

}, 1000 );

	}

	const signals = editor.signals;

	signals.geometryChanged.add( saveState );
	signals.objectAdded.add( saveState );
	signals.objectChanged.add( saveState );
	signals.objectRemoved.add( saveState );
	signals.materialChanged.add( saveState );
	signals.sceneBackgroundChanged.add( saveState );
	signals.sceneEnvironmentChanged.add( saveState );
	signals.sceneFogChanged.add( saveState );
	signals.sceneGraphChanged.add( saveState );
	signals.scriptChanged.add( saveState );
	signals.historyChanged.add( saveState );
	signals.interactionGraphChanged.add( saveState );

} );

//

document.addEventListener( 'dragover', function ( event ) {

	event.preventDefault();
	event.dataTransfer.dropEffect = 'copy';

} );

document.addEventListener( 'drop', function ( event ) {

	event.preventDefault();

	if ( event.dataTransfer.types[ 0 ] === 'text/plain' ) return; // Outliner drop

	if ( event.dataTransfer.items ) {

// DataTransferItemList supports folders

editor.loader.loadItemList( event.dataTransfer.items );

	} else {

editor.loader.loadFiles( event.dataTransfer.files );

	}

} );

function onWindowResize() {

	editor.signals.windowResize.dispatch();

}

window.addEventListener( 'resize', onWindowResize );

onWindowResize();

//

let isLoadingFromHash = false;
const hash = window.location.hash;

if ( hash.slice( 1, 6 ) === 'file=' ) {

	const file = hash.slice( 6 );

	if ( confirm( editor.strings.getKey( 'prompt/file/open' ) ) ) {

const loader = new THREE.FileLoader();
loader.crossOrigin = '';
loader.load( file, function ( text ) {

	editor.clear();
	editor.fromJSON( JSON.parse( text ) );

} );

// Prevent browser back/forward navigation gestures (like Figma)
( function() {

	// Block horizontal wheel events that trigger browser navigation
	function blockHorizontalScroll(e) {
		if (e.deltaX !== 0) {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
	}

	// Add listener at window level with capture phase
	window.addEventListener('wheel', blockHorizontalScroll, { passive: false, capture: true });

	// Prevent keyboard navigation shortcuts
	window.addEventListener( 'keydown', function ( event ) {
		// Prevent Cmd/Ctrl + Arrow keys (browser navigation)
		if ( ( event.metaKey || event.ctrlKey ) && ( event.key === 'ArrowLeft' || event.key === 'ArrowRight' ) ) {
			event.preventDefault();
		}
		// Prevent Backspace navigation (when not in input)
		if ( event.key === 'Backspace' && !( event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' ) ) {
			event.preventDefault();
		}
	}, { passive: false, capture: true } );

} )();

isLoadingFromHash = true;

	}

}

} // End authenticated user initialization

// ServiceWorker - disabled for now due to CDN migration

// if ( 'serviceWorker' in navigator ) {

// 	try {

// 		navigator.serviceWorker.register( 'sw.js' );

// 	} catch ( error ) {

// 	}

// }

